import { Scene } from 'phaser';
import { BotaoArcade } from '../components/BotaoArcade';
import { CampoArcade } from '../components/CampoArcade';
import { desenharFaixaComRessalto, desenharFundo, desenharLogoArgoX } from '../components/cenario';
import { GAME, SCENES } from '../config/constants';
import { ARCADE_TEXT } from '../config/theme';
import { podeVincular } from '../services/fluxo';
import { Sessao } from '../services/Sessao';
import { LoginBloqueadoError } from '../../net/AuthClient';
import { t } from '../services/textos';

const CENTRO_X = 639.5;

/**
 * 1. Acesso à experiência (Figma, quadros 42/43).
 * Vincula o tablet ao participante pelo ID (RN-AP-001).
 */
export class LoginScene extends Scene {
    constructor () {
        super(SCENES.LOGIN);
    }

    create () {
        this.enviando = false;
        const fonte = this.registry.get('fonte');

        // O servidor exige também o código de acesso do participante. O Figma só tem
        // o ID: o campo do código aparece quando a fonte de dados pede (modo `prd`).
        this.exigeCodigo = fonte?.exigeCodigo === true;
        // Nenhum APK nasce com turma fixa e o app não a lembra: o login a pede sempre.
        this.exigeTurma = fonte?.exigeTurma === true;
        const campos = 1 + (this.exigeCodigo ? 1 : 0) + (this.exigeTurma ? 1 : 0);
        const sobe = (campos - 1) * 34;
        const extra = (campos - 1) * 41;

        // Quanto mais campos, mais o conteúdo sobe: a logo Fiat Arcade e a do Novo Fiat Argo X encolhem juntas.
        const logos = { 1: { fiat: 133.5, argo: 236, largura: 420 }, 2: { fiat: 108, argo: 208, largura: 300 }, 3: { fiat: 88, argo: 180, largura: 250 } }[campos];
        desenharFundo(this, { logoY: logos.fiat });
        desenharLogoArgoX(this, logos.argo, logos.largura);
        desenharFaixaComRessalto(this);

        this.add.text(
            CENTRO_X, 272 - sobe,
            campos > 1
                ? t('login.instrucao_com_codigo')
                : t('login.instrucao'),
            { ...ARCADE_TEXT.instrucao, lineSpacing: 2, wordWrap: { width: campos > 1 ? 420 : 369 } }
        ).setOrigin(0.5, 0);

        const aoDigitar = () => this.atualizar();
        const aoEnter = () => this.vincular();

        this.campoId = new CampoArcade(this, CENTRO_X, 396 - sobe, {
            placeholder: t('login.campo_id'), maximo: 40, aoDigitar, aoEnter
        });
        this.campoCodigo = this.exigeCodigo
            ? new CampoArcade(this, CENTRO_X, 478 - sobe, {
                placeholder: t('login.campo_codigo'), tipo: 'password', maximo: 80, aoDigitar, aoEnter
            })
            : null;
        this.campoTurma = this.exigeTurma
            ? new CampoArcade(this, CENTRO_X, (this.exigeCodigo ? 560 : 478) - sobe, {
                placeholder: 'TURMA (EX.: T01)', maximo: 80, aoDigitar, aoEnter
            })
            : null;

        this.erro = this.add.text(CENTRO_X, 460 + extra, '', ARCADE_TEXT.erro).setOrigin(0.5);

        this.botao = new BotaoArcade(this, CENTRO_X, 526 + extra, {
            rotulo: t('login.botao'), habilitado: false, aoClicar: () => this.vincular()
        });

        if (fonte?.modo === 'dev') {
            this.add.text(GAME.WIDTH - 24, GAME.HEIGHT - 16, 'MODO DEV • CONTEÚDO MOCKADO', ARCADE_TEXT.rodape)
                .setOrigin(1, 1);
        }
    }

    preenchido () {
        return podeVincular(this.campoId.valor, {
            exigeCodigo: this.exigeCodigo, codigo: this.campoCodigo?.valor,
            exigeTurma: this.exigeTurma, turma: this.campoTurma?.valor
        });
    }

    /** RI-CRE-01: Vincular só habilita com todos os campos preenchidos. */
    atualizar () {
        this.erro.setText('');
        this.botao.setHabilitado(!this.enviando && this.preenchido());
    }

    async vincular () {
        if (this.enviando || !this.preenchido()) return;

        this.enviando = true;
        this.botao.setHabilitado(false);

        try {
            const sessao = await this.registry.get('fonte').autenticar({
                id: this.campoId.valor, codigo: this.campoCodigo?.valor ?? null,
                turma: this.campoTurma?.valor ?? null
            });
            Sessao.iniciar(sessao);
            this.game.events.emit('sessao:iniciada', sessao);
            // O servidor é a fonte do estado das estações e das fases concluídas: substitui o do tablet.
            await this.registry.get('fonte').recarregarEstadoDoServidor?.();
            // RI-CRE-03: sucesso leva ao Aguarde.
            this.scene.start(SCENES.AGUARDE, {});
        } catch (falha) {
            if (!this.scene.isActive()) return;
            this.enviando = false;
            this.mostrarErro(falha);
            this.botao.setHabilitado(true);
        }
    }

    /** RI-CRE-02: dado inválido mostra mensagem e não vincula. */
    mostrarErro (falha) {
        let mensagem = t('login.erro_conexao');
        if (falha instanceof LoginBloqueadoError) {
            mensagem = t('login.erro_bloqueado', { segundos: falha.esperarSegundos });
        } else if (falha?.name === 'TurmaAusenteError') {
            mensagem = t('login.erro_sem_turma');
        } else if (falha?.name === 'CredencialRecusadaError') {
            mensagem = this.exigeCodigo
                ? t('login.erro_credencial_codigo')
                : t('login.erro_credencial');
        }
        this.erro.setText(mensagem);
        this.cameras.main.shake(180, 0.004);
    }
}
